<template>
  <div>
    <div class="d-flex align-items-center gap-1">
      <span v-if="status" class="badge" :class="badgeClass">
        {{ $t(statusLabelKey) }}
      </span>
      <span v-else class="text-muted">—</span>
    </div>

    <div
      v-if="showApplicationFeePill || showCommunicationPill"
      ref="containerRef"
      class="mt-1 d-flex flex-wrap align-items-center gap-1"
    >
      <span
        v-if="showApplicationFeePill"
        class="badge rounded-pill d-inline-flex align-items-center gap-1 px-2 py-1"
        :class="applicationFeeClass"
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        :data-bs-title="
          $t(
            getFlagLabelKey(
              PlIssuanceFlagKey.ApplicationFeePaid,
              normalizedFlags[PlIssuanceFlagKey.ApplicationFeePaid]
            )
          )
        "
      >
        <font-awesome-icon
          :icon="getFlagIcon(PlIssuanceFlagKey.ApplicationFeePaid)"
        />
        <span class="fw-medium">
          {{
            $t(
              getFlagLabelKey(
                PlIssuanceFlagKey.ApplicationFeePaid,
                normalizedFlags[PlIssuanceFlagKey.ApplicationFeePaid]
              )
            )
          }}
        </span>
      </span>

      <span
        v-if="showCommunicationPill"
        class="badge rounded-pill d-inline-flex align-items-center gap-2 px-2 py-1"
        :class="communicationClass"
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        :data-bs-title="communicationTooltip"
      >
        <font-awesome-icon
          :icon="getFlagIcon(PlIssuanceFlagKey.CommunicationWithTgeInitiated)"
        />
        <span class="fw-medium">
          {{
            $t(
              "issuance_reporting.pl_status_flags.communication_with_tge_initiated"
            )
          }}
        </span>
        <span class="d-inline-flex align-items-center gap-2 ms-1">
          <font-awesome-icon
            v-if="normalizedFlags[PlIssuanceFlagKey.AutomaticWrittenNotice]"
            :icon="getFlagIcon(PlIssuanceFlagKey.AutomaticWrittenNotice)"
            size="sm"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            :data-bs-title="
              $t(
                getFlagLabelKey(
                  PlIssuanceFlagKey.AutomaticWrittenNotice,
                  normalizedFlags[PlIssuanceFlagKey.AutomaticWrittenNotice]
                )
              )
            "
          />
          <font-awesome-icon
            v-if="normalizedFlags[PlIssuanceFlagKey.TelephoneCall]"
            :icon="getFlagIcon(PlIssuanceFlagKey.TelephoneCall)"
            size="sm"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            :data-bs-title="
              $t(
                getFlagLabelKey(
                  PlIssuanceFlagKey.TelephoneCall,
                  normalizedFlags[PlIssuanceFlagKey.TelephoneCall]
                )
              )
            "
          />
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Tooltip } from "bootstrap";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import {
  getApplicationFeePillClass,
  getCommunicationPillClass,
  getFlagIcon,
  getFlagLabelKey,
  getStatusBadgeClass,
  normalizeFlags,
  PlIssuanceFlagKey,
  shouldDisplayFlag,
  statusToLabelKey,
  type PlIssuanceFlags,
  type PlIssuanceStatus,
} from "./plStatusUtils";

const props = withDefaults(
  defineProps<{
    status?: PlIssuanceStatus | null;
    flags?: Partial<PlIssuanceFlags> | null;
  }>(),
  {
    flags: null,
  }
);

const badgeClass = computed(() => getStatusBadgeClass(props.status));
const statusLabelKey = computed(() =>
  props.status ? statusToLabelKey(props.status) : ""
);

const normalizedFlags = computed(() => normalizeFlags(props.flags));

const { t } = useI18n();

const showApplicationFeePill = computed(() =>
  shouldDisplayFlag(
    PlIssuanceFlagKey.ApplicationFeePaid,
    normalizedFlags.value[PlIssuanceFlagKey.ApplicationFeePaid],
    props.status ?? null
  )
);

const showCommunicationPill = computed(() =>
  shouldDisplayFlag(
    PlIssuanceFlagKey.CommunicationWithTgeInitiated,
    normalizedFlags.value[PlIssuanceFlagKey.CommunicationWithTgeInitiated],
    props.status ?? null
  )
);

const applicationFeeClass = computed(() =>
  getApplicationFeePillClass(
    normalizedFlags.value[PlIssuanceFlagKey.ApplicationFeePaid]
  )
);

const communicationClass = computed(() => getCommunicationPillClass());

const communicationTooltip = computed(() => {
  const parentLabel = t(
    "issuance_reporting.pl_status_flags.communication_with_tge_initiated"
  );
  const childParts: string[] = [];
  if (normalizedFlags.value[PlIssuanceFlagKey.AutomaticWrittenNotice]) {
    childParts.push(
      t("issuance_reporting.pl_status_flags.automatic_written_notice")
    );
  }
  if (normalizedFlags.value[PlIssuanceFlagKey.TelephoneCall]) {
    childParts.push(t("issuance_reporting.pl_status_flags.telephone_call"));
  }
  if (childParts.length === 0) {
    return parentLabel;
  }
  return `${parentLabel} (${childParts.join(", ")})`;
});

const containerRef = useTemplateRef<HTMLElement>("containerRef");
let tooltipInstances: Tooltip[] = [];

const cleanupTooltips = () => {
  tooltipInstances.forEach((tooltip) => tooltip.dispose());
  tooltipInstances = [];
};

const initTooltips = () => {
  cleanupTooltips();
  const el = containerRef.value;
  if (!el) {
    return;
  }
  el.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
    tooltipInstances.push(new Tooltip(element));
  });
};

onMounted(() => {
  nextTick(initTooltips);
});

watch(normalizedFlags, () => {
  nextTick(initTooltips);
});

onBeforeUnmount(() => {
  cleanupTooltips();
});
</script>
